import React, { useMemo } from 'react';
import { Menu, MenuProps } from 'antd';
import * as Icons from '@ant-design/icons';
import { NavLink } from 'react-router-dom';
import dayjs from 'dayjs';
import { StyledSider, Brand, InspectionBadge, CounterUpdatedAt } from '../styled';
import { useSidebar } from '../hooks';
import { useAuth } from '@modules/me';
import { useInspectionBadge } from '@modules/branch-inspections';
import { useCounter } from '@modules/counter';

type MenuItem = NonNullable<MenuProps['items']>[number];

const clean = (children: (MenuItem | false)[]): MenuItem[] => children.filter(Boolean) as MenuItem[];

export const AppSidebar = () => {
  const { isOpen, activeKey, setOpen } = useSidebar();
  const { can, canDisplay, hasAnyPermission } = useAuth();
  const { shouldShowBadge } = useInspectionBadge();
  const { state: counter, onMouseEnter, lastUpdatedAt } = useCounter();

  const items: MenuItem[] = useMemo(
    () =>
      clean([
        canDisplay('*') && {
          type: 'group',
          key: 'grp-umumi',
          label: 'Ümumi',
          children: clean([
            can('stat') && { key: '/statistics', icon: <Icons.LineChartOutlined />, label: <NavLink to="/statistics">Statistika</NavLink> },
            can('client_appointment') && { key: '/appointment', icon: <Icons.FileSearchOutlined />, label: <NavLink to="/appointment">Müştəri qəbul</NavLink> },
            can('local_warehouse') && {
              key: '/warehouse/handover/queues',
              icon: <Icons.UserSwitchOutlined />,
              label: (
                <NavLink to="/warehouse/handover/queues">
                  Anbardar təhvil ({counter.handoverQueue.pending}/{counter.handoverQueue.executing})
                </NavLink>
              ),
            },
            can('cashflow') && { key: '/cash-flow', icon: <Icons.MoneyCollectOutlined />, label: <NavLink to="/cash-flow">Cashflow</NavLink> },
          ]),
        },
        canDisplay('*') && {
          type: 'group',
          key: 'grp-esas',
          label: 'Əsas menyu',
          children: clean([
            can('users') && { key: '/users', icon: <Icons.UserOutlined />, label: <NavLink to="/users">Müştərilər</NavLink> },
            can('orders') && {
              key: '/orders',
              icon: <Icons.ShoppingCartOutlined />,
              label: <NavLink to="/orders">Sifarişlər {!!counter.declarations && `(${counter.orders})`}</NavLink>,
            },
            {
              key: '/declarations',
              icon: <Icons.FileTextOutlined />,
              label: <NavLink to="/declarations">Bağlamalar {!!counter.declarations && `(${counter.declarations})`}</NavLink>,
            },
            can('trendyol_declarations') && { key: '/united-declarations', icon: <Icons.FileTextOutlined />, label: <NavLink to="/united-declarations">United Bağlamalar</NavLink> },
            can('united_returns') && { key: '/united-returns', icon: <Icons.RollbackOutlined />, label: <NavLink to="/united-returns">Temu Geri Qaytarmalar</NavLink> },
            can('partner_declarations') && { key: '/declarations/partners', icon: <Icons.FileTextOutlined />, label: <NavLink to="/declarations/partners">Partnyor Bağlamalar</NavLink> },
            can('parcel_sorting_list') && { key: '/sorting', icon: <Icons.DeliveredProcedureOutlined />, label: <NavLink to="/sorting">Filial göndərişləri</NavLink> },
            can('branch_inspections_list') && {
              key: '/branch-inspections',
              icon: <Icons.AuditOutlined />,
              label: (
                <NavLink to="/branch-inspections">
                  Filial yoxlanışları
                  {shouldShowBadge && <InspectionBadge />}
                </NavLink>
              ),
            },
            can('flights') && { key: '/flights', icon: <Icons.RocketOutlined />, label: <NavLink to="/flights">Uçuşlar</NavLink> },
            can('tickets') && {
              key: '/supports',
              icon: <Icons.MessageOutlined />,
              label: <NavLink to="/supports">Müraciətlər {!!counter.supports && `(${counter.supports})`}</NavLink>,
            },
            can('transactions') && { key: '/transactions', icon: <Icons.MoneyCollectOutlined />, label: <NavLink to="/transactions">Balans əməliyyatları</NavLink> },
            can('couriers') && {
              key: '/couriers',
              icon: <Icons.DropboxOutlined />,
              label: <NavLink to="/couriers">Kuryerlər {!!counter.couriers && `(${counter.couriers})`}</NavLink>,
            },
            can('courier_assignments') && { key: '/couriers/deliverer-assignments', icon: <Icons.UserAddOutlined />, label: <NavLink to="/couriers/deliverer-assignments">Kuryer təhkim</NavLink> },
            can('conflicted_declarations') && {
              key: '/declarations/unknowns',
              icon: <Icons.CodeSandboxOutlined />,
              label: <NavLink to="/declarations/unknowns">Mübahisəli bağlamalar {!!counter.unknownDeclarations && `(${counter.unknownDeclarations})`}</NavLink>,
            },
            can('bbs_office') && { key: '/customs/tasks', icon: <Icons.DropboxOutlined />, label: <NavLink to="/customs/tasks">BBS Tapşırıqları</NavLink> },
          ]),
        },
        canDisplay('*') &&
          hasAnyPermission('declarations') && {
            type: 'group',
            key: 'grp-beyannameler',
            label: 'Bəyannamələr',
            children: clean([
              can('dgk_declarations') && { key: '/customs/declarations', icon: <Icons.ExceptionOutlined />, label: <NavLink to="/customs/declarations">Bəyannamələr</NavLink> },
              can('deleted_customs_declarations') && { key: '/declarations/deleted', icon: <Icons.DeleteOutlined />, label: <NavLink to="/declarations/deleted">Silinmiş bəyannamələr</NavLink> },
              can('dgk_declarations') && { key: '/customs/posts', icon: <Icons.AuditOutlined />, label: <NavLink to="/customs/posts">DGK Bağlamalar</NavLink> },
              can('post_declarations') && { key: '/declarations/post', icon: <Icons.FileTextOutlined />, label: <NavLink to="/declarations/post">Bəyan sonrası bağlamalar</NavLink> },
            ]),
          },
        canDisplay('*') &&
          hasAnyPermission('queues') && {
            type: 'group',
            key: 'grp-novbeler',
            label: 'Növbələr',
            children: clean([
              can('bbs_queues') && { key: '/customs/dns-queues', icon: <Icons.OrderedListOutlined />, label: <NavLink to="/customs/dns-queues">BBS növbələri</NavLink> },
              can('united_queues') && { key: '/united-queues', icon: <Icons.DropboxOutlined />, label: <NavLink to="/united-queues">United növbələri</NavLink> },
              can('azerpost_queues') && { key: '/azerpost-queues', icon: <Icons.DropboxOutlined />, label: <NavLink to="/azerpost-queues">Azərpoçt növbələri</NavLink> },
              can('parcel_pin_change') && { key: '/fin-changes', icon: <Icons.IdcardOutlined />, label: <NavLink to="/fin-changes">FİN Düzəlişləri</NavLink> },
            ]),
          },
        canDisplay('*') &&
          hasAnyPermission('notify') && {
            type: 'group',
            key: 'grp-bildirisler',
            label: 'Bildirişlər',
            children: clean([
              can('sms_archive') && { key: '/notifier/sms', icon: <Icons.TabletOutlined />, label: <NavLink to="/notifier/sms">SMS Arxivi</NavLink> },
              can('whatsapp_archive') && { key: '/notifier/whatsapp', icon: <Icons.WhatsAppOutlined />, label: <NavLink to="/notifier/whatsapp">Whatsapp Arxivi</NavLink> },
              can('mail_archive') && { key: '/notifier/email', icon: <Icons.MailOutlined />, label: <NavLink to="/notifier/email">Mail Arxivi</NavLink> },
              can('bulk_app_notification') && { key: '/notifier/mobile/bulk/send', icon: <Icons.NotificationOutlined />, label: <NavLink to="/notifier/mobile/bulk/send">APP bildiriş</NavLink> },
              can('notification_templates') && { key: '/notifier/templates', icon: <Icons.NotificationOutlined />, label: <NavLink to="/notifier/templates">Bildiriş şablonları</NavLink> },
              can('ticket_templates') && { key: '/ticket-templates', icon: <Icons.FileTextOutlined />, label: <NavLink to="/ticket-templates">Müraciət şablonları</NavLink> },
            ]),
          },
        canDisplay('*') &&
          hasAnyPermission('content') && {
            type: 'group',
            key: 'grp-mezmun',
            label: 'Məzmun',
            children: clean([
              can('state_changes') && { key: '/archive/state', icon: <Icons.DatabaseOutlined />, label: <NavLink to="/archive/state">Status Arxivi</NavLink> },
              can('my_logs') && { key: '/logs', icon: <Icons.HistoryOutlined />, label: <NavLink to="/logs">Əməliyyat Arxivi</NavLink> },
              can('news') && { key: '/news', icon: <Icons.ReadOutlined />, label: <NavLink to="/news">Xəbərlər</NavLink> },
              can('faq') && { key: '/faq', icon: <Icons.QuestionCircleOutlined />, label: <NavLink to="/faq">Tez-tez verilən suallar</NavLink> },
              can('shops') && { key: '/shops', icon: <Icons.ShopOutlined />, label: <NavLink to="/shops">Mağazalar</NavLink> },
              can('about') && { key: '/about', icon: <Icons.InfoCircleOutlined />, label: <NavLink to="/about">Haqqında</NavLink> },
              can('transportation_conditions') && { key: '/transportation_conditions', icon: <Icons.SnippetsOutlined />, label: <NavLink to="/transportation_conditions">Daşınma şərtləri</NavLink> },
              can('banners') && { key: '/banners', icon: <Icons.FileImageOutlined />, label: <NavLink to="/banners">Bannerlər</NavLink> },
              can('popups') && { key: '/popups', icon: <Icons.BorderOutlined />, label: <NavLink to="/popups">Popuplar</NavLink> },
              can('delivery_proof') && { key: '/delivery-proofs', icon: <Icons.FileOutlined />, label: <NavLink to="/delivery-proofs">Təhvil sənədləri</NavLink> },
            ]),
          },
        canDisplay('*') &&
          hasAnyPermission('settings') && {
            type: 'group',
            key: 'grp-ayarlar',
            label: 'Proqram ayarları',
            children: clean([
              can('system_settings') && { key: '/settings', icon: <Icons.SettingOutlined />, label: <NavLink to="/settings">Sistem ayarları</NavLink> },
              can('coupons') && { key: '/coupons', icon: <Icons.GiftOutlined />, label: <NavLink to="/coupons">Kuponlar</NavLink> },
              can('returns') && { key: '/refunds', icon: <Icons.RollbackOutlined />, label: <NavLink to="/refunds">İadələr</NavLink> },
              can('cashback') && { key: '/cashback', icon: <Icons.SketchOutlined />, label: <NavLink to="/cashback">Kəşbəklər</NavLink> },
              can('foreign_cargoes') && { key: '/cargoes', icon: <Icons.CodeSandboxOutlined />, label: <NavLink to="/cargoes">Xarici karqolar</NavLink> },
              can('containers') && { key: '/boxes', icon: <Icons.InboxOutlined />, label: <NavLink to="/boxes">Yeşiklər</NavLink> },
              can('shop_names') && { key: '/shop-names', icon: <Icons.ShoppingOutlined />, label: <NavLink to="/shop-names">Xarici Mağazalar</NavLink> },
              can('return_reasons') && { key: '/return-types', icon: <Icons.RollbackOutlined />, label: <NavLink to="/return-types">İadə səbəbləri</NavLink> },
              can('countries') && { key: '/countries', icon: <Icons.CarOutlined />, label: <NavLink to="/countries">Ölkələr</NavLink> },
              can('branches') && { key: '/branches', icon: <Icons.BranchesOutlined />, label: <NavLink to="/branches">Filiallar</NavLink> },
              can('companies') && { key: '/branch-partners', icon: <Icons.BranchesOutlined />, label: <NavLink to="/branch-partners">Şirkətlər</NavLink> },
              can('tarifs') && { key: '/plans', icon: <Icons.FormOutlined />, label: <NavLink to="/plans">Tariflər</NavLink> },
              can('producttypes') && { key: '/product-types', icon: <Icons.BuildOutlined />, label: <NavLink to="/product-types">Məhsul tipləri</NavLink> },
              can('regions') && { key: '/regions', icon: <Icons.TranslationOutlined />, label: <NavLink to="/regions">Rayonlar</NavLink> },
              can('models') && { key: '/models', icon: <Icons.StarOutlined />, label: <NavLink to="/models">Modellər</NavLink> },
              can('states') && { key: '/status', icon: <Icons.WalletOutlined />, label: <NavLink to="/status">Statuslar</NavLink> },
            ]),
          },
        (canDisplay('*') || canDisplay('partner')) && {
          type: 'group',
          key: 'grp-filial',
          label: 'Filial bölmələri',
          children: clean([
            { key: '/partner-boxes', icon: <Icons.InboxOutlined />, label: <NavLink to="/partner-boxes">Yeşiklər</NavLink> },
            { key: '/partner/acceptance/box', icon: <Icons.FileTextOutlined />, label: <NavLink to="/partner/acceptance/box">Bağlama qəbulu</NavLink> },
            can('branch_manager') && { key: '/statistics/branches-partner', icon: <Icons.LineChartOutlined />, label: <NavLink to="/statistics/branches-partner">Yerli anbar statistikası</NavLink> },
          ]),
        },
      ]),
    [can, canDisplay, hasAnyPermission, shouldShowBadge, counter],
  );

  return (
    <React.Fragment>
      <StyledSider trigger={null} collapsible width={224} collapsedWidth={46} collapsed={!isOpen} breakpoint="lg" onBreakpoint={(broken) => setOpen(!broken)}>
        <Brand>{isOpen ? <img src="/logo.svg" alt={process.env.REACT_APP_COMPANY_NAME} /> : <img src="/logo-compact.svg" alt={process.env.REACT_APP_COMPANY_NAME} />}</Brand>
        <Menu onMouseOver={onMouseEnter} theme="dark" mode="inline" selectedKeys={[activeKey]} items={items} />
        {lastUpdatedAt && <CounterUpdatedAt title="Sayğacların son yenilənmə vaxtı">{dayjs(lastUpdatedAt).format('HH:mm:ss')}</CounterUpdatedAt>}
      </StyledSider>
    </React.Fragment>
  );
};
